import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { PartnerProvider } from "./context/PartnerContext";
import { NavbarProvider, useNavbar } from "./context/NavbarContext"; // Nouveau import
import Navbar from "./components/Navbar";
import TopNavbar from "./components/TopNavbar";
import ProtectedRoute from "./components/ProtectedRoute";
import Partners from "./pages/Partners/Partners";
import PartnerDetailLayout from "./pages/Partners/PartnerDetailLayout";
import PositionDetailLayout from "./pages/PositionDetailLayout";
import Offers from "./pages/Offers";
import UserApp from "./pages/HomePage";
import HomeLayout from "./pages/HomeLayout";
import IndividualDetailPage from "./pages/IndividualDetailPage";
import IndividualsLayout from "./pages/IndividualsLayout";
import Settings from "./pages/Settings";
import Todos from "./pages/Todos";
import LoginSignup from "./pages/LoginSignup";
import LegalMentions from "./components/client/LegalMentions";
import PrivacyPolicy from "./pages/client/PrivacyPolicy";
import TermsOfService from "./pages/client/TermsOfService";
import Home from "./pages/client/Home";
import { AuthProvider } from "./context/AuthContext";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import EmailVerify from "./pages/EmailVerify";
import ResetPassword from "./pages/ResetPassword";
import { AppContextProvider } from "./context/AppContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminTab from "./pages/AdminTab";
import Reminders from "./components/Reminders/Reminders";
import ContactForm from "./components/client/ContactForm";
import CVtheque from "./pages/client/CVtheque";
import Reporting from "./pages/client/Reporting";
import Evaluation from "./pages/client/Evaluation";
import Extension from "./pages/client/Extension";
import Filtres from "./pages/client/Filtres";
import Documentation from "./pages/client/Documentation";
import Tutorials from "./pages/client/Tutorials";
import Blog from "./pages/client/Blog";
import Support from "./pages/client/Support";
import Deals from "./pages/Deals";
import BlogArticle from "./pages/client/BlogArticle";
import Analytics from "./pages/Analytics";
import ScrapingPage from "./pages/ScrapingPage";
import NewPosition from "./pages/NewPosition";
import Messaging from "./pages/Messaging";
import Mailing from "./pages/Mailing";
import ProcessBizDevDetail from "./pages/ProcessBizDevDetail";
import ProcessBizDevList from "./components/processBizDev/ProcessBizDevList";
import SharedProcessBizDevBoard from "./pages/SharedProcessBizDevBoard";
import SharedProcessBoard from "./pages/SharedProcessBoard";
import ProcessBizDevLayout from "./pages/ProcessBizDevLayout";
import JoinCompany from "./pages/JoinCompany";

// 🔧 PRODUCTION READY API Configuration
const getBackendURL = () => {
  // Debug: Log environment variables
  console.log("🔍 Environment check:", {
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
    available_env_vars: Object.keys(process.env).filter((key) =>
      key.startsWith("REACT_APP_")
    ),
  });

  // Use environment variable if available
  if (process.env.REACT_APP_BACKEND_URL) {
    console.log(
      "✅ Using REACT_APP_BACKEND_URL:",
      process.env.REACT_APP_BACKEND_URL
    );
    return process.env.REACT_APP_BACKEND_URL;
  }

  // Fallback based on environment
  const fallbackUrl =
    process.env.NODE_ENV === "development"
      ? "http://localhost:5000"
      : "https://cognito-back-production.up.railway.app";

  console.log("⚠️ Using fallback URL:", fallbackUrl);
  return fallbackUrl;
};

const API = axios.create({
  baseURL: getBackendURL(),
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true, // 🔧 CRITICAL: Enable cookies for session-based auth
  timeout: 30000, // 30 second timeout for production
});

// Request interceptor - add JWT token if available
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("auth-token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Debug logging for production issues
    if (process.env.NODE_ENV === "development") {
      console.log("🔗 API Request:", config.method?.toUpperCase(), config.url);
    }

    return config;
  },
  (error) => {
    console.error("❌ API Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors globally
API.interceptors.response.use(
  (response) => {
    // Debug successful responses in development
    if (process.env.NODE_ENV === "development") {
      console.log(
        "✅ API Response:",
        response.config.method?.toUpperCase(),
        response.config.url,
        response.status
      );
    }
    return response;
  },
  async (error) => {
    // Handle specific error cases
    if (error.response?.status === 401) {
      console.error("❌ Token expired or unauthorized. Redirecting to login");

      // Clear local storage and session
      localStorage.removeItem("auth-token");
      sessionStorage.clear();

      // Try to call logout API if available
      try {
        await API.post("/auth/logout");
      } catch (logoutError) {
        console.log("Logout API call failed, proceeding with local logout");
      }

      // Only redirect if not already on a public page
      const currentPath = window.location.pathname;
      const publicPaths = [
        "/",
        "/login",
        "/mentions-legales",
        "/reset-password",
        "/email-verify",
      ];

      if (!publicPaths.includes(currentPath)) {
        window.location.href = "/";
      }
    } else if (error.response?.status === 403) {
      console.error("❌ Access forbidden");
    } else if (error.response?.status >= 500) {
      console.error("❌ Server error:", error.response.status);
    } else if (error.code === "NETWORK_ERROR" || !error.response) {
      console.error("❌ Network error - check backend connection");
    }

    return Promise.reject(error);
  }
);

window.API = API;

// ✅ Stocker API dans le contexte React au lieu de `window.API`
export const ApiContext = React.createContext(API);

const AppContent = () => {
  const location = useLocation();
  // ✅ FIX: Toujours utiliser la largeur de la navbar RÉDUITE (64px)
  // La navbar étendue (au hover) se superpose au contenu via position fixed
  const NAVBAR_WIDTH_COLLAPSED = 64; // Correspond à w-16 en Tailwind
  const [isDesktop, setIsDesktop] = React.useState(true);

  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const pathsWithoutNavbar = [
    "/",
    "/login",
    "/mentions-legales",
    "/reset-password",
    "/email-verify",
    "/contact",
    "/reporting",
    "/cvtheque",
    "/evaluation",
    "/extension",
    "/filtres",
    "/docs",
    "/tutorials",
    "/blog",
    "/support",
    "/blog/",
    "/blog/processus-recrutement-optimise-2025",
    "/blog/guide-sourcing-linkedin-2025",
    "/blog/ia-intelligence-artificielle-recrutement-2025",
    "/privacy-policy",
    "/terms-of-service",
  ];

  // Check if path starts with /join-company/ (for invitation links)
  const isJoinCompanyPath = location.pathname.startsWith("/join-company/");

  // Vérifier si le chemin est dans la liste ou commence par /shared/ ou /join-company/
  const shouldShowNavbar =
    !pathsWithoutNavbar.includes(location.pathname) &&
    !location.pathname.startsWith("/shared/") &&
    !isJoinCompanyPath;

  return (
    <div style={{ display: "flex" }}>
      {shouldShowNavbar && <Navbar />}
      <div
        style={{
          // ✅ FIX: Utiliser la largeur constante de la navbar réduite
          // Même au hover, le contenu ne bouge pas - la navbar se superpose
          marginLeft:
            shouldShowNavbar && isDesktop ? `${NAVBAR_WIDTH_COLLAPSED}px` : "0",
          width:
            shouldShowNavbar && isDesktop
              ? `calc(100% - ${NAVBAR_WIDTH_COLLAPSED}px)`
              : "100%",
          minHeight: "100vh",
          // Pas de transition nécessaire car la largeur ne change plus
        }}
      >
        {shouldShowNavbar && <TopNavbar />}
        <ToastContainer />
        <Routes>
          {/* Routes publiques */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<LoginSignup />} />
          <Route path="/mentions-legales" element={<LegalMentions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/email-verify" element={<EmailVerify />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/contact" element={<ContactForm />} />
          <Route path="/cvtheque" element={<CVtheque />} />
          <Route path="/reporting" element={<Reporting />} />
          <Route path="/evaluation" element={<Evaluation />} />
          <Route path="/extension" element={<Extension />} />
          <Route path="/filtres" element={<Filtres />} />
          <Route path="/docs" element={<Documentation />} />
          <Route path="/tutorials" element={<Tutorials />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/support" element={<Support />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />

          {/* Route publique d'invitation */}
          <Route path="/join-company/:token" element={<JoinCompany />} />

          {/* Routes publiques pour les processus partagés */}
          <Route
            path="/shared/process-bizdev/:shareToken"
            element={<SharedProcessBizDevBoard />}
          />
          <Route
            path="/shared/position/:shareToken"
            element={<SharedProcessBoard />}
          />

          {/* Routes protégées */}
          <Route path="/partners" element={<ProtectedRoute><Partners /></ProtectedRoute>} />

          {/* Routes imbriquées pour les partners avec effet sidebar */}
          <Route path="/partners/:id" element={<ProtectedRoute><PartnerDetailLayout /></ProtectedRoute>}>
            <Route index element={null} />
            <Route
              path="contacts/:contactId"
              element={<IndividualDetailPage />}
            />
          </Route>

          <Route path="/process-rh" element={<ProtectedRoute><Offers /></ProtectedRoute>} />
          <Route path="/prospection" element={<ProtectedRoute><ProcessBizDevList /></ProtectedRoute>} />

          {/* Routes imbriquées pour les positions avec effet sidebar */}
          <Route path="/positions/:id" element={<ProtectedRoute><PositionDetailLayout /></ProtectedRoute>}>
            <Route index element={null} />
            <Route
              path="candidates/:candidateId"
              element={<IndividualDetailPage />}
            />
          </Route>

          {/* Routes imbriquées pour les individuals avec effet sidebar */}
          <Route path="/all-individuals" element={<ProtectedRoute><IndividualsLayout /></ProtectedRoute>}>
            <Route index element={null} />
            <Route path=":id" element={<IndividualDetailPage />} />
          </Route>

          {/* Routes imbriquées pour les process bizdev avec effet sidebar */}
          <Route
            path="/process/bizdev/:processId"
            element={<ProtectedRoute><ProcessBizDevLayout /></ProtectedRoute>}
          >
            <Route index element={null} />
            <Route
              path="candidates/:candidateId"
              element={<IndividualDetailPage />}
            />
          </Route>

          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

          {/*
            <Route path="/todo" element={<ProtectedRoute><Todos /></ProtectedRoute>} />
            <Route path="/scraping" element={<ProtectedRoute><ScrapingPage /></ProtectedRoute>} />
            <Route path="/messaging" element={<ProtectedRoute><Messaging /></ProtectedRoute>} />
            <Route path="/mailing" element={<ProtectedRoute><Mailing /></ProtectedRoute>} />
          */}

          {/* Routes imbriquées pour la HomePage avec effet sidebar */}
          <Route path="/app" element={<ProtectedRoute><HomeLayout /></ProtectedRoute>}>
            <Route index element={null} />
            <Route
              path="contacts/:contactId"
              element={<IndividualDetailPage />}
            />
          </Route>
          <Route path="/admin" element={<ProtectedRoute><AdminTab /></ProtectedRoute>} />
          <Route path="/reminders" element={<ProtectedRoute><Reminders /></ProtectedRoute>} />
          <Route path="/deals" element={<ProtectedRoute><Deals /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
          <Route path="/offers/new" element={<ProtectedRoute><NewPosition /></ProtectedRoute>} />
        </Routes>
      </div>
    </div>
  );
};

const App = () => (
  <Router>
    <AuthProvider>
      <PartnerProvider>
        <NavbarProvider>
          {" "}
          {/* Nouveau provider */}
          <ApiContext.Provider value={API}>
            <AppContextProvider>
              <AppContent />
            </AppContextProvider>
          </ApiContext.Provider>
        </NavbarProvider>
      </PartnerProvider>
    </AuthProvider>
  </Router>
);

export default App;
